import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { inviteAthlete, type InviteAthleteError } from "api-client";
import { FormProvider as Form, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "ui";

import { formSchema } from "src/invitations/AssignEmailToAthleteDialog.shared";
import type {
  FormSchema,
  AssignEmailToAthleteDialogContentProps,
} from "src/invitations/AssignEmailToAthleteDialog.shared";

export function AssignEmailToAthleteDialogContent(
  props: AssignEmailToAthleteDialogContentProps,
) {
  const { t } = useTranslation();
  const inviteClientMutation = useMutation({
    mutationFn: (arg: FormSchema) => {
      return inviteAthlete({
        body: {
          athleteId: props.athleteId,
          email: arg.email,
        },
      }).then((response) => {
        return response.data.data;
      });
    },
  });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  async function handleSubmit(values: FormSchema) {
    if (inviteClientMutation.isPending) {
      return;
    }

    inviteClientMutation.mutate(values, {
      onSuccess: (response) => {
        if (response.id) {
          props.onInviteSuccess?.(response);
        }

        props.closeModal();
      },
      onError: (err: unknown) => {
        form.setError("email", {
          message: (err as InviteAthleteError).error.message,
        });
      },
    });
  }

  return (
    <Form {...form}>
      <form
        className="space-y-4 md:space-y-6"
        action="#"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <DialogHeader>
          <DialogTitle>{t("assignEmailDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("assignEmailDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("inviteAthleteDialog.fields.email.label")}{" "}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    "inviteAthleteDialog.fields.email.placeholder",
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="submit" loading={inviteClientMutation.isPending}>
            {t("inviteAthleteDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
